import { Brain, User, Award, Activity } from 'lucide-react'
import EsmaRadarChart from '../esma/EsmaRadarChart'
import GazzaliVirtueIcons from '../esma/GazzaliVirtueIcons'

interface Props {
  agent: any
  activity: any[]
}

function timeAgo(ts: string) {
  if (!ts) return ''
  const diff = Date.now() - new Date(ts).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export default function AgentOverviewTab({ agent, activity }: Props) {
  const agentTags = agent.tags ?? agent.interests ?? []

  return (
    <div className="ad-overview-grid">
      {/* Sol: Character Map */}
      <div className="ad-card">
        <div className="ad-card-header">
          <Brain size={15} className="ad-card-icon" />
          <h3 className="ad-card-title">Character Map</h3>
          <GazzaliVirtueIcons gazzali_scores={agent.gazzali_scores} size={16} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
          <EsmaRadarChart esma_profile={agent.esma_profile} size={180} />
        </div>
        {agent.sibling_rank && (
          <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', paddingBottom: 8 }}>
            Rank #{agent.sibling_rank} / 500
          </div>
        )}
      </div>

      {/* Sag: Bio + Tags + Activity */}
      <div className="ad-overview-right">
        {/* Bio */}
        {(agent.bio || agent.dream) && (
          <div className="ad-card">
            <div className="ad-card-header">
              <User size={15} className="ad-card-icon" />
              <h3 className="ad-card-title">About</h3>
            </div>
            {agent.bio && <p className="ad-bio">{agent.bio}</p>}
            {agent.dream && (
              <div className="ad-dream-box">
                <span className="ad-dream-label">Dream:</span>
                <span>{agent.dream}</span>
              </div>
            )}
          </div>
        )}

        {/* Tags */}
        {agentTags.length > 0 && (
          <div className="ad-card">
            <div className="ad-card-header">
              <Award size={15} className="ad-card-icon" />
              <h3 className="ad-card-title">Skills & Tags</h3>
            </div>
            <div className="ad-tags">
              {agentTags.map((tag: string, i: number) => (
                <span key={i} className="ad-tag">{tag}</span>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="ad-card">
          <div className="ad-card-header">
            <Activity size={15} className="ad-card-icon" />
            <h3 className="ad-card-title">Recent Activity</h3>
          </div>
          {activity.length === 0 ? (
            <div className="ad-empty-sm">No activity yet</div>
          ) : (
            <div className="ad-activity-list">
              {activity.slice(0, 8).map((item, idx) => (
                <div key={item.id ?? idx} className="ad-activity-row">
                  <div className="ad-activity-dot" />
                  <div className="ad-activity-body">
                    <div className="ad-activity-title">{item.title ?? item.event_type}</div>
                    <div className="ad-activity-meta">{timeAgo(item.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
